import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Práce ve výškách (> 1,5 m) - V1 authored osnova. Czech-only (LocalizedText
 * cs-CZ). 4 chapters across the full block catalog + a graded quiz with one
 * question per kind. Answer keys live here (repo-privileged) and in the DB;
 * stripped from the client payload by the API.
 */
export const workAtHeightsProfile: TrainingProfile = {
  slug: 'work-at-heights',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Práce ve výškách (> 1,5 m)',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-zaklady',
        title: t('Základy a právní rámec', 'Basics and legal framework'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-zaklady',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Práce ve výškách', 'Work at heights'),
          },
          {
            id: 'p-zaklady-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Za práci ve výšce se považuje práce a pohyb pracovníka, při němž je **ohrožen pádem z výšky, do hloubky, propadnutím nebo sesutím**. Ochrana je nutná již od výšky **1,5 m** nad okolní úrovní.',
              'Work at height is any work and movement of a worker in which they are **at risk of falling from a height, into a depth, through a surface, or by a collapse**. Protection is required already from a height of **1.5 m** above the surrounding level.',
            ),
          },
          {
            id: 'c-zaklady-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **NV 362/2005 Sb.** o bližších požadavcích na bezpečnost a ochranu zdraví při práci na pracovištích s nebezpečím pádu z výšky nebo do hloubky.',
              'Legal basis: **Gov. Reg. 362/2005 Coll.** on detailed requirements for occupational safety and health at workplaces with a risk of falling from a height or into a depth.',
            ),
          },
          {
            id: 'k-zaklady',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Ochrana proti pádu je povinná od **1,5 m**. Vždy upřednostni **kolektivní** ochranu před osobní.',
              'Fall protection is mandatory from **1.5 m**. Always prefer **collective** protection over personal protection.',
            ),
          },
        ],
      },
      {
        id: 'ch-ochrana',
        title: t('Prostředky ochrany proti pádu', 'Fall protection equipment'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-ochrana'],
        blocks: [
          {
            id: 'h-ochrana',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Kolektivní a osobní ochrana', 'Collective and personal protection'),
          },
          {
            id: 'p-ochrana-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**Kolektivní ochrana** (zábradlí, ochranné sítě, lešení) chrání všechny pracovníky bez jejich aktivní součinnosti, a má proto vždy přednost. **Osobní ochranné prostředky proti pádu (OOPP)** se použijí, kde kolektivní ochranu nelze zajistit.',
              '**Collective protection** (guardrails, safety nets, scaffolding) protects all workers without their active involvement and therefore always takes precedence. **Personal fall-protection equipment (PPE)** is used where collective protection cannot be provided.',
            ),
          },
          {
            id: 'cl-ochrana',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Celotělový postroj odpovídající ČSN EN 361',
                'Full-body harness conforming to ČSN EN 361',
              ),
              t('Tlumič pádu a spojovací prostředek', 'Shock absorber and connecting lanyard'),
              t(
                'Kotvicí bod o pevnosti min. 12 kN',
                'Anchor point with a strength of at least 12 kN',
              ),
              t(
                'Vizuální kontrola OOPP před každým použitím',
                'Visual inspection of the PPE before each use',
              ),
            ],
          },
          {
            id: 'c-ochrana-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Poškozený postroj nebo spojovací prostředek **okamžitě vyřaď z provozu**. Po zachycení pádu nesmí být OOPP znovu použity.',
              'Take a damaged harness or connecting lanyard **out of service immediately**. After arresting a fall, the PPE must not be used again.',
            ),
          },
          {
            id: 'kc-ochrana',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Který typ ochrany má vždy přednost?',
              'Which type of protection always takes precedence?',
            ),
            choices: [
              { id: 'kc-a', label: t('Kolektivní ochrana', 'Collective protection') },
              {
                id: 'kc-b',
                label: t('Osobní ochranné prostředky', 'Personal protective equipment'),
              },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Kolektivní ochrana chrání všechny pracovníky bez jejich aktivní součinnosti.',
              'Collective protection protects all workers without their active involvement.',
            ),
          },
          {
            id: 'hs-postroj',
            blockType: 'hotspotScene',
            schemaVersion: 1,
            scene: 'harnessPoints',
            hotspots: [
              {
                id: 'hp-d',
                x: 0.5,
                y: 0.36,
                label: t('Zádový kotvicí bod (D-kroužek)', 'Dorsal anchor point (D-ring)'),
              },
              { id: 'hp-stehna', x: 0.5, y: 0.71, label: t('Stehenní popruhy', 'Thigh straps') },
            ],
          },
        ],
      },
      {
        id: 'ch-postup',
        title: t('Bezpečný pracovní postup', 'Safe working procedure'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-postup'],
        blocks: [
          {
            id: 'h-postup',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Postup před zahájením práce', 'Procedure before starting work'),
          },
          {
            id: 'anim-zebrik',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'ladderAngle',
            steps: [
              {
                label: t('Příliš strmý', 'Too steep'),
                say: t('Žebřík se překlopí dozadu.', 'The ladder tips backwards.'),
              },
              {
                label: t('Správně ≈ 75°', 'Right ≈ 75°'),
                say: t(
                  'Pravidlo 4 : 1 - čtyři díly výšky, jeden díl od stěny; nad výstupní úroveň přesahuje nejméně 1,1 m (NV 362/2005 Sb.).',
                  'The 4 : 1 rule - four parts up, one part out; it extends at least 1.1 m above the landing (NV 362/2005).',
                ),
              },
              {
                label: t('Příliš plochý', 'Too shallow'),
                say: t(
                  'Pata podklouzne a žebřík spadne.',
                  'The foot slides out and the ladder falls.',
                ),
              },
            ],
            caption: t(
              'Žebřík opři pod úhlem přibližně 75° - pravidlo 4 : 1 (čtyři díly výšky, jeden díl od stěny).',
              'Lean the ladder at an angle of approximately 75° - the 4 : 1 rule (four parts of height, one part out from the wall).',
            ),
          },
          {
            id: 'ord-postup',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky před zahájením práce ve výšce do správného pořadí.',
              'Put the steps before starting work at height into the correct order.',
            ),
            items: [
              {
                id: 'op-1',
                label: t('Posouzení rizik a kontrola počasí', 'Risk assessment and weather check'),
              },
              {
                id: 'op-2',
                label: t('Kontrola a nasazení OOPP', 'Inspecting and putting on the PPE'),
              },
              {
                id: 'op-3',
                label: t(
                  'Ukotvení do certifikovaného kotvicího bodu',
                  'Anchoring to a certified anchor point',
                ),
              },
              { id: 'op-4', label: t('Zahájení práce', 'Starting the work') },
            ],
          },
          {
            id: 'c-postup-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Nikdy nepracuj ve výšce sám** tam, kde hrozí pád, bez možnosti přivolání pomoci. Vždy musí existovat plán záchrany.',
              '**Never work at height alone** where a fall is possible and help cannot be summoned. A rescue plan must always be in place.',
            ),
          },
        ],
      },
      {
        id: 'ch-zachrana',
        title: t('Záchrana a první pomoc', 'Rescue and first aid'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-zachrana',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Plán záchrany', 'Rescue plan'),
          },
          {
            id: 'p-zachrana-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Po zachycení pádu hrozí **závěsové trauma** - pracovník visící v postroji musí být sejmut **do 15 minut**. Plán záchrany musí být připraven před zahájením práce.',
              'After a fall is arrested there is a risk of **suspension trauma** - a worker hanging in a harness must be taken down **within 15 minutes**. The rescue plan must be ready before work begins.',
            ),
          },
          {
            id: 'acc-zachrana',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t('Co je závěsové trauma?', 'What is suspension trauma?'),
                a: t(
                  'Stav způsobený nehybným visením v postroji, kdy se krev hromadí v dolních končetinách a hrozí selhání oběhu.',
                  'A condition caused by hanging motionless in a harness, in which blood pools in the lower limbs and circulatory failure may occur.',
                ),
              },
              {
                q: t('Jak postupovat při záchraně?', 'How to proceed during a rescue?'),
                a: t(
                  'Co nejrychleji sejmout postroj nebo zajistit odlehčení, přivolat ZZS na lince 155 a sledovat životní funkce.',
                  'Remove the harness or provide relief as quickly as possible, call the emergency medical service on 155, and monitor vital signs.',
                ),
              },
            ],
          },
          {
            id: 'k-zachrana',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Bez **plánu záchrany** nesmí práce ve výšce začít. Tísňová linka: **155**.',
              'Work at height must not begin without a **rescue plan**. Emergency line: **155**.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-vyska',
      kind: 'numeric',
      chapterId: 'ch-zaklady',
      prompt: t(
        'Od jaké výšky (v metrech) nad okolní úrovní je povinná ochrana proti pádu?',
        'From what height (in metres) above the surrounding level is fall protection mandatory?',
      ),
      correct: 1.5,
      tolerance: 0,
    },
    {
      id: 'q-prednost',
      kind: 'single',
      chapterId: 'ch-ochrana',
      prompt: t(
        'Který druh ochrany má vždy přednost?',
        'Which type of protection always takes precedence?',
      ),
      choices: [
        { id: 'a', label: t('Osobní ochranné prostředky', 'Personal protective equipment') },
        { id: 'b', label: t('Kolektivní ochrana', 'Collective protection') },
        { id: 'c', label: t('Bezpečnostní značení', 'Safety signage') },
      ],
      correct: 'b',
    },
    {
      id: 'q-postroj-tf',
      kind: 'truefalse',
      chapterId: 'ch-ochrana',
      prompt: t(
        'OOPP po zachycení pádu lze po vizuální kontrole znovu použít.',
        'PPE can be reused after a visual inspection once it has arrested a fall.',
      ),
      correct: false,
    },
    {
      id: 'q-oopp-multi',
      kind: 'multi',
      chapterId: 'ch-ochrana',
      prompt: t(
        'Které prvky patří mezi OOPP proti pádu? (vyber všechny správné)',
        'Which items are fall-protection PPE? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Celotělový postroj', 'Full-body harness') },
        { id: 'b', label: t('Tlumič pádu', 'Shock absorber') },
        { id: 'c', label: t('Pracovní rukavice', 'Work gloves') },
        { id: 'd', label: t('Spojovací prostředek', 'Connecting lanyard') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-postup-order',
      kind: 'ordering',
      chapterId: 'ch-postup',
      prompt: t(
        'Seřaď kroky před zahájením práce ve výšce.',
        'Order the steps before starting work at height.',
      ),
      choices: [
        { id: 'a', label: t('Posouzení rizik', 'Risk assessment') },
        { id: 'b', label: t('Nasazení OOPP', 'Putting on the PPE') },
        { id: 'c', label: t('Ukotvení', 'Anchoring') },
        { id: 'd', label: t('Zahájení práce', 'Starting the work') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
